import { useLocation, useNavigate } from 'react-router-dom';
import { Sun, Moon, Search, ChevronDown, User, LogOut, CreditCard, Settings, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useThemeStore } from '@/store/useThemeStore';
import { useEnvironmentStore } from '@/store/useEnvironmentStore';
import { useAuthStore } from '@/store/useAuthStore';

const ENV_COLORS = {
  green: 'bg-green-500',
  yellow: 'bg-yellow-500',
  red: 'bg-red-500',
  blue: 'bg-blue-500',
};

const BREADCRUMB_MAP = {
  '/': 'Dashboard',
  '/environments': 'Environments',
  '/authentication': 'Authentication',
  '/error-explorer': 'Error Explorer',
  '/search': 'Search',
  '/settings': 'Settings',
  '/merchant-api': 'Merchant API',
  '/collection-api': 'Collection API',
};

export default function Topbar() {
  const { theme, toggleTheme } = useThemeStore();
  const { environments, activeEnvId } = useEnvironmentStore();
  const merchant = useAuthStore((s) => s.merchant);
  const logout = useAuthStore((s) => s.logout);
  const location = useLocation();
  const navigate = useNavigate();

  const handleSignOut = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const activeEnv = environments.find((e) => e.id === activeEnvId);
  const pageTitle = BREADCRUMB_MAP[location.pathname] || 'Developer Portal';

  return (
    <header className="h-14 border-b border-border bg-background/95 backdrop-blur-sm flex items-center justify-between px-4 shrink-0 sticky top-0 z-20">
      {/* Left: breadcrumb */}
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold text-foreground">{pageTitle}</span>
      </div>

      {/* Right: actions */}
      <div className="flex items-center gap-2">
        {/* Search */}
        <Button
          variant="outline"
          size="sm"
          className="hidden md:flex items-center gap-2 text-muted-foreground w-48 justify-start"
          onClick={() => navigate('/search')}
        >
          <Search className="h-3.5 w-3.5" />
          <span className="text-xs">Search...</span>
          <kbd className="ml-auto pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
            ⌘K
          </kbd>
        </Button>

        {/* Environment indicator — locked to whatever was chosen at login; switching
            hosts mid-session would leave requests using a token from the wrong environment */}
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center gap-2 rounded-md border border-border px-3 h-8 text-muted-foreground">
              <span className={`h-2 w-2 rounded-full ${activeEnv ? ENV_COLORS[activeEnv.color] : 'bg-muted'}`} />
              <span className="text-xs">{activeEnv?.name || 'No Environment'}</span>
              <Lock className="h-3 w-3 opacity-50" />
            </div>
          </TooltipTrigger>
          <TooltipContent side="bottom">Environment is locked for this session — sign out to switch.</TooltipContent>
        </Tooltip>

        {/* Theme toggle */}
        <Button variant="ghost" size="icon" onClick={toggleTheme} className="h-8 w-8">
          {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </Button>

        {/* User menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="gap-2 h-8">
              <div className="h-6 w-6 rounded-full bg-primary/20 flex items-center justify-center">
                <User className="h-3.5 w-3.5 text-primary" />
              </div>
              <span className="text-xs font-medium hidden md:block">{merchant?.name || merchant?.companyName || 'Account'}</span>
              <ChevronDown className="h-3.5 w-3.5 opacity-50" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuLabel>
              <p className="font-medium">{merchant?.companyName || merchant?.name || 'Merchant Account'}</p>
              <p className="text-xs text-muted-foreground font-normal">{merchant?.email || ''}</p>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate('/settings')}>
              <Settings className="h-3.5 w-3.5" />
              Settings
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate('/authentication')}>
              <CreditCard className="h-3.5 w-3.5" />
              Authentication
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="text-destructive" onClick={handleSignOut}>
              <LogOut className="h-3.5 w-3.5" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
